import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Home, ArrowLeft, DollarSign, TrendingUp, Clock, CheckCircle2 } from 'lucide-react';

export default function LandlordEarningsPage() {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchEarnings(); }, []);

  const fetchEarnings = async () => {
    try {
      const response = await axios.get(`${API}/landlord/earnings`, { headers: { Authorization: `Bearer ${token}` } });
      setData(response.data);
    } catch (error) {
      toast.error('Failed to fetch earnings');
    } finally { setLoading(false); }
  };

  if (loading) return <div className="flex items-center justify-center min-h-screen">Loading...</div>;

  return (
    <div className="min-h-screen bg-background">
      <header className="glass-header bg-white/70 border-b border-border/40 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-3 sm:py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-4">
            <Button data-testid="back-btn" onClick={() => navigate(-1)} variant="outline" size="sm" className="rounded-full">
              <ArrowLeft className="w-4 h-4 mr-2" />Back
            </Button>
            <div className="flex items-center gap-2">
              <Home className="w-6 h-6 text-primary" />
              <span className="text-xl font-bold tracking-tight text-primary">PrimePocket</span>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-8">
        <div className="mb-8">
          <div className="flex items-center gap-3">
            <DollarSign className="w-8 h-8 text-primary" />
            <h1 className="text-xl sm:text-2xl md:text-3xl font-medium tracking-tight">Earnings & Payouts</h1>
          </div>
          <p className="text-muted-foreground mt-2">Track your rental income and platform fees</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="p-6 border border-border shadow-sm bg-primary/5" data-testid="stat-total-earnings">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Earnings</p>
                <p className="text-3xl font-bold mt-2 text-primary">${(data?.total_earnings || 0).toFixed(2)}</p>
              </div>
              <TrendingUp className="w-10 h-10 text-primary opacity-40" />
            </div>
          </Card>
          <Card className="p-6 border border-border shadow-sm" data-testid="stat-pending-payout">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pending Payout</p>
                <p className="text-3xl font-bold mt-2 text-yellow-600">${(data?.pending_payout || 0).toFixed(2)}</p>
              </div>
              <Clock className="w-10 h-10 text-yellow-600 opacity-40" />
            </div>
          </Card>
          <Card className="p-6 border border-border shadow-sm" data-testid="stat-paid-out">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Paid Out</p>
                <p className="text-3xl font-bold mt-2 text-green-600">${(data?.paid_out || 0).toFixed(2)}</p>
              </div>
              <CheckCircle2 className="w-10 h-10 text-green-600 opacity-40" />
            </div>
          </Card>
          <Card className="p-6 border border-border shadow-sm" data-testid="stat-platform-fees">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Platform Fees</p>
                <p className="text-3xl font-bold mt-2 text-accent">${(data?.total_platform_fees || 0).toFixed(2)}</p>
              </div>
              <DollarSign className="w-10 h-10 text-accent opacity-40" />
            </div>
          </Card>
        </div>

        <Card className="border border-border overflow-hidden">
          <div className="p-4 border-b border-border">
            <h2 className="font-medium">Transactions</h2>
          </div>
          {(!data?.transactions || data.transactions.length === 0) ? (
            <div className="p-12 text-center text-muted-foreground">
              <DollarSign className="w-12 h-12 mx-auto mb-3 opacity-40" />
              No paid transactions yet
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted">
                  <tr>
                    <th className="text-left p-4 font-medium">Property</th>
                    <th className="text-left p-4 font-medium">Tenant</th>
                    <th className="text-right p-4 font-medium">Total Paid</th>
                    <th className="text-right p-4 font-medium">Platform Fee</th>
                    <th className="text-right p-4 font-medium">Your Payout</th>
                    <th className="text-center p-4 font-medium">Status</th>
                    <th className="text-left p-4 font-medium">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {data.transactions.map((t, idx) => (
                    <tr key={t.session_id} className="border-t border-border" data-testid={`txn-${idx}`}>
                      <td className="p-4 font-medium">{t.property_title}</td>
                      <td className="p-4 text-muted-foreground">{t.user_name}</td>
                      <td className="p-4 text-right">${(t.amount || 0).toFixed(2)}</td>
                      <td className="p-4 text-right text-accent">-${(t.platform_fee || 0).toFixed(2)}</td>
                      <td className="p-4 text-right font-semibold text-primary">${(t.landlord_payout_amount || 0).toFixed(2)}</td>
                      <td className="p-4 text-center">
                        {t.payout_status === 'paid_out' ? (
                          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700"><CheckCircle2 className="w-3 h-3" />Paid</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700"><Clock className="w-3 h-3" />Pending</span>
                        )}
                      </td>
                      <td className="p-4 text-muted-foreground text-xs">{new Date(t.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <div className="mt-6 p-4 bg-muted/50 rounded-lg text-sm text-muted-foreground">
          <p><strong>Note:</strong> Payouts are automatically calculated and pending until processed. In production with Stripe Connect enabled, payouts transfer directly to your bank account 2 business days after each successful payment.</p>
        </div>
      </div>
    </div>
  );
}
